import { useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Alert, Button, Card, cx } from '../../components/ui';
import {
  ApiError,
  UNLIMITED_HOURS,
  api,
  formatHours,
  formatPrice,
  type Plan,
  type Subscription as Sub,
} from '../../lib/api';
import { useLiveUsage } from '../../lib/liveUsage';
import { usePageTitle } from '../../lib/usePageTitle';
import { PanelState } from './DashboardLayout';

/**
 * Plan selection is governed by the server, not by a flag in this file.
 *
 * There used to be a `PAID_PLAN_SELECTION_DISABLED` constant here that
 * disabled every paid button, with a comment explaining how to switch it back
 * on. It was the right call while nothing could take a payment, and the wrong
 * shape: the page decided for itself what the API would accept, and the two
 * could only agree by someone remembering to edit both.
 *
 * `subscription.can_select_paid_plans` is now the answer, computed on the
 * server from the account's promotional window and whether a payment gateway
 * is configured. This file renders it.
 */

/** Opens in a new tab so a half-made purchase decision is not navigated away from. */
function LegalLink({ to, children }: { to: string; children: ReactNode }) {
  return (
    <Link
      to={to}
      target="_blank"
      rel="noopener noreferrer"
      className="text-surge-400 underline underline-offset-2 hover:text-surge-300"
    >
      {children}
    </Link>
  );
}

function formatDate(value: string): string {
  return new Date(value).toLocaleDateString(undefined, { dateStyle: 'medium' });
}

export function Subscription() {
  usePageTitle('Subscription — WaveQA');

  const queryClient = useQueryClient();
  const [pending, setPending] = useState<Plan | null>(null);
  const [error, setError] = useState<string | null>(null);

  // This page shows the hours bar and the remaining balance, so it needs the
  // same out-of-band freshness as the overview. Plans are static content.
  const { subscription } = useLiveUsage();
  const plans = useQuery({ queryKey: ['plans'], queryFn: api.getPlans });

  const change = useMutation({
    mutationFn: (plan: string) => api.changePlan(plan),
    onSuccess: () => {
      setPending(null);
      setError(null);
      void queryClient.invalidateQueries({ queryKey: ['subscription'] });
      // A downgrade revokes the credentials beyond the new agent limit, in the
      // same transaction as the plan change. Invalidating only the
      // subscription left the credentials list and the overview's agent tile
      // showing those credentials as active until something else happened to
      // refetch them — the customer reads "1 of 1 agent" beside two live
      // credentials, one of which has already stopped authenticating.
      void queryClient.invalidateQueries({ queryKey: ['credentials'] });
    },
    onError: (err) =>
      setError(err instanceof ApiError ? err.message : 'Could not change the plan.'),
  });

  const current = subscription.data;
  // Three states, not two. Until the subscription has loaded the answer is
  // *unknown*, and defaulting it to "no" would flash "Payment required" at a
  // customer whose promotion is running - the one message they must not be
  // shown. Unknown renders as a plain disabled button with no claim on it.
  const canSelectPaid = current ? current.can_select_paid_plans : null;

  return (
    <div>
      <h2 className="text-lg font-semibold text-white">Subscription</h2>
      <p className="mt-1 max-w-xl text-sm text-hull-400">
        Your plan, entitlements and usage for the current period.
      </p>

      {error && <div className="mt-4"><Alert tone="error">{error}</Alert></div>}

      <div className="mt-6">
        <PanelState loading={subscription.isPending} error={subscription.error}>
          {current && <CurrentPlan subscription={current} />}
        </PanelState>
      </div>

      <h3 className="mb-4 mt-10 text-base font-semibold text-white">Change plan</h3>

      <PanelState loading={plans.isPending} error={plans.error}>
        <ul className="grid gap-3 sm:grid-cols-2">
          {plans.data?.map((plan) => {
            const isCurrent = current?.plan === plan.plan;
            const free = plan.monthlyPriceMinorUnits === 0;
            // Free is always selectable, so an account is never stranded on a
            // plan it cannot leave and cannot pay for.
            const selectable = free || canSelectPaid === true;
            const paymentNeeded = !free && canSelectPaid === false;
            return (
              <Card
                as="li"
                key={plan.plan}
                className={cx('flex flex-col', isCurrent && 'border-surge-600/60')}
              >
                <div className="flex items-baseline justify-between gap-2">
                  <h4 className="text-base font-semibold text-white">{plan.name}</h4>
                  <span className="text-lg font-semibold text-white">
                    {formatPrice(plan.monthlyPriceMinorUnits, plan.currency)}
                  </span>
                </div>
                <p className="mt-2 flex-1 text-sm text-hull-400">
                  {plan.agentLimit} {plan.agentLimit === 1 ? 'agent' : 'agents'} ·{' '}
                  {plan.unlimitedHours
                    ? 'unlimited hours per month'
                    : `${plan.monthlyHours} hours per month`}
                </p>
                {!free && current?.promotion_active && (
                  <p className="mt-2 text-xs text-pass-400">
                    Included in your promotional period
                  </p>
                )}
                <div className="mt-4">
                  {isCurrent ? (
                    <span className="text-sm text-surge-400">Current plan</span>
                  ) : plan.contactSales ? (
                    <Button variant="secondary" disabled className="w-full">
                      Contact sales
                    </Button>
                  ) : selectable ? (
                    <Button
                      variant="secondary"
                      className="w-full"
                      onClick={() => setPending(plan)}
                    >
                      Switch to {plan.name}
                    </Button>
                  ) : paymentNeeded ? (
                    <Button
                      variant="secondary"
                      disabled
                      className="w-full"
                      title="Online payment is not open yet."
                    >
                      Payment required
                    </Button>
                  ) : (
                    <Button variant="secondary" disabled className="w-full">
                      Switch to {plan.name}
                    </Button>
                  )}
                </div>
              </Card>
            );
          })}
        </ul>
        {canSelectPaid === false && (
          <p className="mt-4 text-xs text-hull-400">
            Paid plans need a payment, and online payment is not open yet. We
            will email you as soon as it is. The free plan stays available in
            the meantime.
          </p>
        )}
      </PanelState>

      {/* Spec 63: plan changes must be confirmed before execution. */}
      {pending && (
        <div className="mt-5 rounded-card border border-hull-600 bg-hull-800 p-5">
          <h4 className="text-base font-semibold text-white">
            Switch to {pending.name}?
          </h4>
          <p className="mt-2 text-sm text-hull-300">
            {pending.monthlyPriceMinorUnits === 0 ? (
              <>
                Your plan changes immediately. Agent limit becomes{' '}
                {pending.agentLimit}; existing credentials beyond that limit keep
                working but you will not be able to create more.
              </>
            ) : current?.promotion_active ? (
              <>
                Your promotional period covers this plan, so it starts working
                straight away and nothing is charged. When the promotion ends
                {current.promotion_ends_at
                  ? ` on ${formatDate(current.promotion_ends_at)}`
                  : ''}
                , this plan will need a payment — you can move back to the free
                plan at any time before then.
              </>
            ) : (
              <>
                Selecting a paid plan records your choice but does{' '}
                <strong className="text-white">not</strong> take payment or
                activate it. The plan becomes active only when the payment
                provider confirms — until then it sits as{' '}
                <code className="text-warn-400">pending</code> and agent runs stay
                on your current entitlement.
              </>
            )}
          </p>
          {/* The terms and the refund policy belong in front of the buyer at
              the point of purchase, not merely somewhere on the site. Shown for
              paid plans only — there is nothing to refund on Free, and nothing
              is charged during the promotion either, but the terms still govern
              the plan being taken up. */}
          {pending.monthlyPriceMinorUnits !== 0 && (
            <p className="mt-3 text-xs leading-relaxed text-hull-400">
              Paid plans are sold directly by us. Continuing means you accept
              the <LegalLink to="/terms">Terms of Service</LegalLink> and{' '}
              <LegalLink to="/refund-policy">Refund Policy</LegalLink>.
            </p>
          )}
          <div className="mt-4 flex gap-2">
            <Button onClick={() => change.mutate(pending.plan)} disabled={change.isPending}>
              {change.isPending ? 'Changing…' : 'Confirm'}
            </Button>
            <Button variant="secondary" onClick={() => setPending(null)}>
              Cancel
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * The promotional window, stated plainly.
 *
 * Rendered from `promotion_days_remaining` rather than from a date difference
 * computed here: the server rounds the last partial day up, and a browser in
 * another timezone subtracting two ISO strings would disagree with it by a day
 * at exactly the moment the number matters most.
 */
function PromotionBanner({ subscription }: { subscription: Sub }) {
  if (subscription.requires_payment) {
    return (
      <Alert tone="warn">
        <strong className="text-white">Your promotional period has ended</strong>
        {subscription.promotion_ends_at
          ? ` (${formatDate(subscription.promotion_ends_at)}).`
          : '.'}{' '}
        The {subscription.plan} plan needs a payment to keep running, and online
        payment is not open yet — we will email you the moment it is. Switch to
        the free plan below to carry on running the agent in the meantime.
      </Alert>
    );
  }

  if (!subscription.promotion_active) return null;

  const days = subscription.promotion_days_remaining;
  const ends = subscription.promotion_ends_at;

  return (
    <Alert tone="info">
      <strong className="text-white">Launch promotion</strong> — every plan is
      free for you until {ends ? formatDate(ends) : 'the end of your window'}
      {days !== null ? ` (${days} ${days === 1 ? 'day' : 'days'} left)` : ''}.
      Switch between plans as often as you like; nothing is charged until the
      promotion ends, and we will email you before it does.
    </Alert>
  );
}

function CurrentPlan({ subscription }: { subscription: Sub }) {
  const used = subscription.hours_used_this_period;
  const allowance = subscription.monthly_hour_allowance;
  // An unlimited allowance has no meaningful bar to fill, so the meter is
  // replaced by the running total rather than pinned near zero forever.
  const unlimited = allowance >= UNLIMITED_HOURS;
  const percent = allowance > 0 ? Math.min(100, (used / allowance) * 100) : 0;

  const statusTone =
    {
      active: 'text-pass-400 border-pass-600/40',
      trialing: 'text-pass-400 border-pass-600/40',
      pending: 'text-warn-400 border-warn-600/40',
      past_due: 'text-fail-400 border-fail-600/40',
      canceled: 'text-hull-400 border-hull-600',
      paused: 'text-hull-400 border-hull-600',
    }[subscription.status] ?? 'text-hull-400 border-hull-600';

  return (
    <Card>
      <div className="flex flex-wrap items-center gap-3">
        <h3 className="text-xl font-semibold capitalize text-white">{subscription.plan}</h3>
        <span className={cx('rounded-full border px-2.5 py-0.5 text-xs', statusTone)}>
          {/* `trialing` is what a plan running on the promotion is. Named for
              what it means to the customer rather than for the column value,
              which reads as a free trial of something they did not start. */}
          {subscription.status === 'trialing' && subscription.promotion_active
            ? 'promotional'
            : subscription.status.replace('_', ' ')}
        </span>
        {!subscription.is_entitled && (
          <span className="text-xs text-fail-400">Agent runs are not authorised</span>
        )}
      </div>

      <div className="mt-4">
        <PromotionBanner subscription={subscription} />
      </div>

      {subscription.status === 'pending' && !subscription.requires_payment && (
        <div className="mt-4">
          <Alert tone="info">
            This plan is awaiting payment confirmation. WaveQA never activates a
            paid plan on the browser&rsquo;s word — only a verified payment event
            does that.
          </Alert>
        </div>
      )}

      <div className="mt-6">
        <div className="flex items-baseline justify-between text-sm">
          <span className="text-hull-300">Execution hours this period</span>
          <span className="text-hull-200">
            <strong className="text-white">{used}</strong> of{' '}
            {formatHours(allowance)} used
          </span>
        </div>
        {!unlimited && (
          <div
            className="mt-2 h-2 overflow-hidden rounded-full bg-hull-700"
            role="progressbar"
            aria-valuenow={Math.round(percent)}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label="Execution hours used this period"
          >
            <div
              className={cx(
                'h-full rounded-full transition-all',
                percent >= 100 ? 'bg-fail-400' : percent > 80 ? 'bg-warn-400' : 'bg-surge-500',
              )}
              style={{ width: `${percent}%` }}
            />
          </div>
        )}
        <p className="mt-1.5 text-xs text-hull-400">
          {unlimited
            ? 'Your plan includes unlimited hours.'
            : `${subscription.hours_remaining} hours remaining`}
        </p>
      </div>

      <dl className="mt-6 grid gap-x-6 gap-y-2 border-t border-hull-700 pt-5 text-sm sm:grid-cols-2">
        <Row
          label="Agents"
          value={`${subscription.agents_in_use} of ${subscription.agent_limit} in use`}
        />
        <Row
          label="Period ends"
          value={
            subscription.current_period_end
              ? formatDate(subscription.current_period_end)
              : '—'
          }
        />
        {subscription.promotion_ends_at && (
          <Row
            label={subscription.promotion_active ? 'Promotion ends' : 'Promotion ended'}
            value={formatDate(subscription.promotion_ends_at)}
          />
        )}
        {subscription.cancel_at && (
          <Row
            label="Cancels"
            value={new Date(subscription.cancel_at).toLocaleDateString()}
          />
        )}
      </dl>
    </Card>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-3">
      <dt className="text-hull-400">{label}</dt>
      <dd className="text-hull-200">{value}</dd>
    </div>
  );
}
